import React, { useEffect, useMemo, useRef } from 'react';
import { BdsIcon } from '@/core/constants/webComponents';

import { Menu, type MenuItem } from '@/features/menu/components/Menu/Menu';

import './MetricsMenu.scss';

export interface MetricsMenuItem {
  id: string;
  label: string;
  path?: string;
  href?: string;
  icon?: string;
  secondaryText?: string;
  disabled?: boolean;
  disabledReason?: 'upgrade' | 'permission';
  visible?: boolean | (() => boolean);
  target?: string;
  defaultOpen?: boolean;
  children?: MetricsMenuItem[];
}

interface MetricsMenuProps {
  items: MetricsMenuItem[];
  channelName: string;
  channel?: string;
  channelExternalUrl: string;
  username?: string;
  pageId?: string;
  currentPath?: string;
  eventName?: string;
  onNavigate?: (path: string) => void;
}

const buildItemHref = (path: string, pageId?: string): string => {
  if (!pageId || !path.startsWith('/')) return path;
  const search = globalThis.location?.search ?? '';
  return `${path.replace(/\/$/, '')}/${encodeURIComponent(pageId)}/${search}`;
};

const INTERNAL_MENU_EVENT = 'mfe-metrics-menu-internal-click';

const isItemVisible = (item: MetricsMenuItem): boolean => {
  return (typeof item.visible === 'function' ? item.visible() : item.visible) !== false;
};

const toMenuItems = (items: MetricsMenuItem[], pageId?: string): MenuItem[] => {
  return items.filter(isItemVisible).map((item) => {
    const path = item.path ?? item.href;
    return {
      ...item,
      path,
      href: path ? buildItemHref(item.href ?? path, pageId) : undefined,
      children: item.children ? toMenuItems(item.children, pageId) : undefined,
    };
  });
};

export const MetricsMenu: React.FC<MetricsMenuProps> = ({
  items,
  channelName,
  channel = '',
  channelExternalUrl,
  username,
  pageId,
  currentPath,
  eventName = 'mfe-metrics-menu-item-click',
  onNavigate,
}) => {
  const navRef = useRef<HTMLElement | null>(null);
  const menuItems = useMemo(() => toMenuItems(items, pageId), [items, pageId]);
  const isGoogleMyBusiness = channel === 'googlemybusiness';
  let address = username?.trim() ?? '';
  if (address) {
    const lastOpenParen = address.lastIndexOf('(');
    if (lastOpenParen !== -1) {
      address = address.substring(0, lastOpenParen).trim();
    }
  }

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const handleInternalClick = (event: Event) => {
      event.stopPropagation();
      const { id, path, label } = (event as CustomEvent).detail;
      onNavigate?.(path);
      nav.dispatchEvent(
        new CustomEvent(eventName, {
          detail: { id, path, label, channel, pageId },
          bubbles: true,
          composed: true,
        }),
      );
    };

    nav.addEventListener(INTERNAL_MENU_EVENT, handleInternalClick);
    return () => nav.removeEventListener(INTERNAL_MENU_EVENT, handleInternalClick);
  }, [eventName, onNavigate, channel, pageId]);

  return (
    <nav ref={navRef} className={`metrics-menu${isGoogleMyBusiness ? ' metrics-menu--googlemybusiness' : ''}`} aria-label={channelName}>
      <div className={`metrics-menu__header${isGoogleMyBusiness ? ' metrics-menu__header--googlemybusiness' : ''}`}>
        <div className="metrics-menu__channel-info">
          <span className="metrics-menu__channel">{channelName}</span>
          {isGoogleMyBusiness && address && (
            <span className="metrics-menu__address">{address}</span>
          )}
        </div>
        {!isGoogleMyBusiness && channelExternalUrl && (
          <a
            className="metrics-menu__external-link"
            href={channelExternalUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={channelName}
            onClick={(e) => e.currentTarget.dispatchEvent(new CustomEvent('mfe-metrics-external-link-click', { detail: { channel: channelName, url: channelExternalUrl }, bubbles: true, composed: true }))}
          >
            <div className="metrics-menu__external-icon">
              <BdsIcon name="external-file" theme="outline" size="small"/>
            </div>
          </a>
        )}
      </div>
      <Menu
        items={menuItems}
        variant="metrics"
        eventName={INTERNAL_MENU_EVENT}
        initialActivePath={currentPath}
      />
    </nav>
  );
};
